// GuideOverlay - ピースビルダー共通のガイド（スポットライト＋吹き出し）
//
// ビルダーの Modal と同じ ScreenModal のコンテナへ描く。そのためゲーム機の画面から
// はみ出さず、拡大縮小も frame から継承する（手動 scale を当てない）。
// スポットライトは案内したい要素の id から getBoundingClientRect() で毎回実測し、
// frame 内の座標に直して使う。固定座標を持たないのでレイアウトが変わってもズレない。
// 画面に無い対象のステップは自動で飛ばす。
// 吹き出しは対象の空いている側に出し、frame 内へクランプしたうえで、しっぽで対象と繋ぐ。
// ガワは他の Modal と同じ BuilderModalPanel。

import { createPortal } from "react-dom";
import { useRef, useState, useEffect } from "react";
import I18n from "common/i18n";
import { btnDelay, usePlaySound } from "common/utils";
import { useScreenModalContainer } from "./ScreenModal";
import { BuilderModalPanel, BuilderModalAccent } from "./BuilderModalPanel";
import { PillSize, PillColor, BuilderPillButton } from "./BuilderPillButton";

export type GuideStep = { targetId: string; titleKey: string; descKey: string };

type Rect = { top: number; left: number; width: number; height: number };

// 暗幕の色（Tailwind の色トークンに black が無いため inline で指定する）
// 当てている対象が薄いグレーでも浮いて見えるよう、濃いめにする
const SCRIM = "rgba(0,0,0,0.75)";
const SPOTLIGHT_PADDING = 8;
// タイトル（type-md = 27px）が12文字まで1行に収まる幅。内側 = 幅 - 132px
const BUBBLE_WIDTH = 480;
const BUBBLE_GAP = 16;
const FRAME_MARGIN = 12;
const TAIL_SIZE = 16;
// しっぽが角に被らないよう、吹き出しの辺の端から空ける距離
const TAIL_INSET = 24;

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), Math.max(min, max));

// 対象要素の位置を frame 内の座標（scale を戻した値）で返す
const measure = (targetId: string, container: HTMLElement): Rect | null => {
  const el = document.getElementById(targetId);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 || r.height === 0) return null;
  const c = container.getBoundingClientRect();
  const scale = container.offsetWidth ? c.width / container.offsetWidth : 1;
  return {
    top: (r.top - c.top) / scale - SPOTLIGHT_PADDING,
    left: (r.left - c.left) / scale - SPOTLIGHT_PADDING,
    width: r.width / scale + SPOTLIGHT_PADDING * 2,
    height: r.height / scale + SPOTLIGHT_PADDING * 2,
  };
};

enum BubbleSide {
  ABOVE,
  BELOW,
  LEFT,
  RIGHT,
}

export const GuideOverlay = ({
  open,
  steps: allSteps,
  onClose,
}: {
  open: boolean;
  steps: GuideStep[];
  onClose: () => void;
}) => {
  const play = usePlaySound();
  const container = useScreenModalContainer();
  const bubbleRef = useRef<HTMLDivElement>(null);
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const [bubbleHeight, setBubbleHeight] = useState(0);

  // 画面に出ている対象だけをステップにする
  const steps = allSteps.filter((s) =>
    open && container ? measure(s.targetId, container) !== null : true,
  );
  const current = steps[Math.min(step, Math.max(steps.length - 1, 0))];

  useEffect(() => {
    if (open) setStep(0);
  }, [open]);

  // 対象はパネルの開閉やレイアウトの確定で後から動く。開いたときだけ測ると
  // 穴が元の位置に残り、対象が暗幕の下に沈む。開いている間は毎フレーム測り直し、
  // 値が変わったときだけ更新する
  useEffect(() => {
    if (!open || !current || !container) return;
    let frame = 0;
    const tick = () => {
      const next = measure(current.targetId, container);
      setRect((prev) =>
        prev &&
        next &&
        prev.top === next.top &&
        prev.left === next.left &&
        prev.width === next.width &&
        prev.height === next.height
          ? prev
          : next,
      );
      frame = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(frame);
  }, [open, current, container]);

  useEffect(() => {
    if (bubbleRef.current) setBubbleHeight(bubbleRef.current.offsetHeight);
  }, [current, rect]);

  if (!open || !current || !container) return null;

  const isLast = step >= steps.length - 1;

  const handleNext = () => {
    play();
    btnDelay(() => (isLast ? onClose() : setStep(step + 1)));
  };

  const handleSkip = () => {
    play();
    btnDelay(onClose);
  };

  const frameWidth = container.offsetWidth;
  const frameHeight = container.offsetHeight;

  // 吹き出しを出す側を決める。縦長の対象は横に、横長の対象は縦に置くと重なりにくい
  const spaceAbove = rect ? rect.top - BUBBLE_GAP - FRAME_MARGIN : 0;
  const spaceBelow = rect ? frameHeight - (rect.top + rect.height) - BUBBLE_GAP - FRAME_MARGIN : 0;
  const spaceLeft = rect ? rect.left - BUBBLE_GAP - FRAME_MARGIN : 0;
  const spaceRight = rect ? frameWidth - (rect.left + rect.width) - BUBBLE_GAP - FRAME_MARGIN : 0;
  const vertical = spaceBelow >= spaceAbove ? BubbleSide.BELOW : BubbleSide.ABOVE;
  const horizontal = spaceRight >= spaceLeft ? BubbleSide.RIGHT : BubbleSide.LEFT;
  const verticalFits = Math.max(spaceAbove, spaceBelow) >= bubbleHeight;
  const horizontalFits = Math.max(spaceLeft, spaceRight) >= BUBBLE_WIDTH;
  const preferHorizontal = rect ? rect.height > rect.width : false;
  const side = preferHorizontal
    ? horizontalFits || !verticalFits
      ? horizontal
      : vertical
    : verticalFits || !horizontalFits
      ? vertical
      : horizontal;

  let bubbleTop = frameHeight / 2 - bubbleHeight / 2;
  let bubbleLeft = frameWidth / 2 - BUBBLE_WIDTH / 2;
  if (rect) {
    if (side === BubbleSide.BELOW || side === BubbleSide.ABOVE) {
      bubbleTop =
        side === BubbleSide.BELOW
          ? rect.top + rect.height + BUBBLE_GAP
          : rect.top - BUBBLE_GAP - bubbleHeight;
      bubbleLeft = rect.left + rect.width / 2 - BUBBLE_WIDTH / 2;
    } else {
      bubbleLeft =
        side === BubbleSide.RIGHT
          ? rect.left + rect.width + BUBBLE_GAP
          : rect.left - BUBBLE_GAP - BUBBLE_WIDTH;
      bubbleTop = rect.top + rect.height / 2 - bubbleHeight / 2;
    }
  }
  bubbleTop = clamp(bubbleTop, FRAME_MARGIN, frameHeight - bubbleHeight - FRAME_MARGIN);
  bubbleLeft = clamp(bubbleLeft, FRAME_MARGIN, frameWidth - BUBBLE_WIDTH - FRAME_MARGIN);

  // しっぽは対象の中心に合わせ、吹き出しの辺からはみ出さないようクランプする
  const tailOffset = TAIL_SIZE / 2;
  const tailStyle: React.CSSProperties = !rect
    ? { display: "none" }
    : side === BubbleSide.BELOW || side === BubbleSide.ABOVE
      ? {
          left: clamp(
            rect.left + rect.width / 2 - bubbleLeft - tailOffset,
            TAIL_INSET,
            BUBBLE_WIDTH - TAIL_INSET - TAIL_SIZE,
          ),
          ...(side === BubbleSide.BELOW ? { top: -tailOffset } : { bottom: -tailOffset }),
        }
      : {
          top: clamp(
            rect.top + rect.height / 2 - bubbleTop - tailOffset,
            TAIL_INSET,
            Math.max(TAIL_INSET, bubbleHeight - TAIL_INSET - TAIL_SIZE),
          ),
          ...(side === BubbleSide.RIGHT ? { left: -tailOffset } : { right: -tailOffset }),
        };

  return createPortal(
    <div className="flex-col-el absolute inset-0 pointer-events-auto" onClick={handleSkip}>
      {/* 暗幕: スポットライトの上下左右を4枚の帯で覆う。
          clip-path の穴あけは指定を1文字間違えると全く描かれない事故が起きるため使わない */}
      {rect ? (
        <>
          {/* 寸法は 0 未満にしない。対象が画面の端にあると負になり、ブラウザが無効として捨てて
              前のステップの値が残る（左の帯が 768px のまま残り、暗幕が二重になっていた） */}
          <div
            className="absolute left-0 right-0 top-0"
            style={{ height: Math.max(0, rect.top), background: SCRIM }}
          />
          <div
            className="absolute left-0 right-0 bottom-0"
            style={{ top: Math.max(0, rect.top + rect.height), background: SCRIM }}
          />
          <div
            className="absolute left-0"
            style={{
              top: Math.max(0, rect.top),
              width: Math.max(0, rect.left),
              height: Math.max(0, rect.height),
              background: SCRIM,
            }}
          />
          <div
            className="absolute right-0"
            style={{
              top: Math.max(0, rect.top),
              left: Math.max(0, rect.left + rect.width),
              height: Math.max(0, rect.height),
              background: SCRIM,
            }}
          />
          <div
            className="absolute box-border rounded-2xl border-solid border-4 border-yellow-100 pointer-events-none"
            style={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }}
          />
        </>
      ) : (
        <div className="absolute inset-0" style={{ background: SCRIM }} />
      )}

      {/* 吹き出し。ガワは他の Modal と同じ BuilderModalPanel */}
      <div
        ref={bubbleRef}
        onClick={(e) => e.stopPropagation()}
        className="flex-col-el absolute"
        style={{ top: bubbleTop, left: bubbleLeft, width: BUBBLE_WIDTH }}
      >
        {/* しっぽ。パネルと同じ白なので、回転した四角を辺に半分だけ出して繋げる */}
        <div className="flex-col-el absolute w-4 h-4 bg-white rotate-45" style={tailStyle} />

        <BuilderModalPanel
          accent={BuilderModalAccent.BLUE}
          icon="menu_book"
          title={I18n.t(current.titleKey)}
          width={BUBBLE_WIDTH}
          onClose={handleSkip}
        >
          <p className="type-text-left type-body text-textcolor-black w-full">
            {I18n.t(current.descKey)}
          </p>

          {/* ステップドット（1ステップだけのページでは出さない） */}
          {steps.length > 1 && (
            <div className="flex-row-el flex-center w-full gap-2">
              {steps.map((s, i) => (
                <div
                  key={s.targetId}
                  className={`w-3 h-3 rounded-full ${i === step ? "bg-blue-100" : "bg-gray2-30"}`}
                />
              ))}
            </div>
          )}

          <div className="flex-row-el items-center justify-between w-full pb-2">
            <BuilderPillButton
              color={PillColor.BLUE}
              size={PillSize.MD}
              icon="schedule"
              label={I18n.t("MSG_BUILDER_TUTORIAL_SKIP")}
              onClick={handleSkip}
            />
            <BuilderPillButton
              color={PillColor.GREEN}
              size={PillSize.MD}
              icon={isLast ? "check" : "chevron_right"}
              label={
                isLast ? I18n.t("MSG_BUILDER_TOP_GUIDE_CLOSE") : I18n.t("MSG_BUILDER_TUTORIAL_NEXT")
              }
              onClick={handleNext}
            />
          </div>
        </BuilderModalPanel>
      </div>
    </div>,
    container,
  );
};
